import { useSuspenseQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Outlet,
  useMatchRoute,
  useNavigate,
} from "@tanstack/react-router";
import { Eye } from "lucide-react";
import { PageLayout } from "~/components/page-layout";
import { Button } from "~/components/ui/button";
import { trpc } from "~/lib/trpc";
import { cn } from "~/lib/utils";
import { EditableVariantName } from "~/routes/(app)/pages/$pageId/variants/$variantId/-components/editable-variant-name";
import { OpenStudioButton } from "~/routes/(app)/pages/$pageId/variants/$variantId/-components/open-studio-button";
import { VariantActionsDropdown } from "~/routes/(app)/pages/$pageId/variants/$variantId/-components/variant-actions-dropdown";

export const Route = createFileRoute(
  "/(app)/pages/$pageId/variants/$variantId",
)({
  component: RouteComponent,
  loader: async ({ context: { queryClient }, params }) => {
    const [{ variant }, status] = await Promise.all([
      queryClient.ensureQueryData(
        trpc.variants.getVariant.queryOptions({
          variantId: params.variantId,
          pageId: params.pageId,
        }),
      ),
      queryClient.ensureQueryData(
        trpc.variants.getVariantStatus.queryOptions({
          variantId: params.variantId,
        }),
      ),
    ]);

    return {
      variant,
      status,
      crumb: {
        id: params.variantId,
        label: variant.name,
        options: [],
      },
    };
  },
});

function RouteComponent() {
  const navigate = useNavigate();
  const matchRoute = useMatchRoute();

  const { variant } = Route.useLoaderData();
  const {
    data: { status },
  } = useSuspenseQuery(
    trpc.variants.getVariantStatus.queryOptions({
      variantId: variant.id,
    }),
  );

  return (
    <PageLayout.Main>
      <div className="max-w-[900px] mx-auto mb-12">
        <div className="mb-3 grid grid-cols-[auto_1fr] gap-9 items-center">
          <EditableVariantName
            savedName={variant.name}
            variantId={variant.id}
            pageId={variant.pageId}
          />

          <div className="flex items-center gap-2 w-full justify-end">
            <VariantActionsDropdown
              pageId={variant.pageId}
              variantId={variant.id}
            />
            <PreviewButton previewUrl={variant.previewUrl} />
            <OpenStudioButton studioUrl={variant.studioUrl} />
          </div>
        </div>
        <ul className="flex gap-5 border-b mb-5">
          <li>
            <TabButton
              selected={
                !!matchRoute({
                  to: "/pages/$pageId/variants/$variantId",
                })
              }
              onClick={() =>
                navigate({
                  to: "/pages/$pageId/variants/$variantId",
                  params: { pageId: variant.pageId, variantId: variant.id },
                })
              }
            >
              Overview
            </TabButton>
          </li>
          <li>
            <TabButton
              selected={
                !!matchRoute({
                  to: "/pages/$pageId/variants/$variantId/targeting",
                })
              }
              className="flex items-center gap-1.5"
              onClick={() =>
                navigate({
                  to: "/pages/$pageId/variants/$variantId/targeting",
                  params: {
                    pageId: variant.pageId,
                    variantId: variant.id,
                  },
                })
              }
            >
              Targeting
              {status === "live" ? (
                <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 bg-green-100 rounded-full transition-colors duration-150">
                  <span className="size-1 bg-green-600 rounded-full"></span>
                  <span className="text-green-600">Live</span>
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 bg-slate-100 rounded-full transition-colors duration-150">
                  <span className="size-1.5 bg-slate-500 rounded-full"></span>
                  <span className="text-slate-500">Inactive</span>
                </span>
              )}
            </TabButton>
          </li>
        </ul>

        <Outlet />
      </div>
    </PageLayout.Main>
  );
}

function TabButton({
  children,
  selected,
  className,
  onClick,
}: {
  children: React.ReactNode;
  selected?: boolean;
  className?: string;
  onClick?: () => void;
}) {
  return (
    <button
      className={cn(
        "text-sm text-gray-500 border-b hover:text-gray-800 py-2 transition-colors duration-150 -mb-px",
        !selected && "border-transparent hover:border-gray-400/75",
        selected && "text-gray-800 border-gray-800",
        className,
      )}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function PreviewButton({ previewUrl }: { previewUrl: string }) {
  return (
    <Button variant="outline" size="sm" asChild>
      <a href={previewUrl} target="_blank" rel="noreferrer">
        <Eye className="text-gray-400" />
        Preview
      </a>
    </Button>
  );
}
